import { Button, Icon } from "@sellar-io/components";
import { useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Alert } from "../../../components/alert/Alert";
import { useBasketContext } from "../../../hooks";
import { useStockListContext } from "../useStockListContext";
import { useIneligibleForCourierStore } from "../../../hooks/useIneligibleForCourierStore";
import { ProfilePicture } from "../../../components";
import { useStorefrontPromotion } from "../categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionProvider.shared";
import { ContainerTypeEligibilityContainerTypesType } from "@sellar-io/api-v2/types";
import { isIneligibleForFulfillmentType } from "@sellar-io/common";

export const CourierUnavailableSelector = ({
  updateFulfillmentType,
}: {
  updateFulfillmentType: () => void;
}) => {
  const [active, setActive] = useState(false);

  return (
    <>
      <div
        className="flex cursor-pointer items-center gap-4 p-4 hover:bg-zinc-100"
        onClick={() => setActive(true)}
      >
        <Icon icon="ALERT" className="text-yellow-600" />
        <div className="flex flex-col">
          <span className="font-semibold">Courier unavailable</span>
          <span className="text-sm">Remove incompatible items</span>
        </div>
        <Icon icon="CHEVRON_RIGHT" size={1} className="ml-auto" />
      </div>
      <CourierUnavailableDialog
        active={active}
        onDismiss={() => setActive(false)}
        updateFulfillmentType={updateFulfillmentType}
      />
    </>
  );
};

const CourierUnavailableDialog = ({
  active,
  onDismiss,
  updateFulfillmentType,
}: {
  active: boolean;
  onDismiss: () => void;
  updateFulfillmentType: () => void;
}) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        title="Remove items to switch to courier delivery"
        description={`Some items in your basket cannot be delivered by courier`}
      />
      {active && (
        <Content
          onDismiss={onDismiss}
          updateFulfillmentType={updateFulfillmentType}
        />
      )}
    </Dialog>
  );
};

const Content = ({
  onDismiss,
  updateFulfillmentType,
}: {
  onDismiss: () => void;
  updateFulfillmentType: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [{ order, modifyOrderItem }] = useBasketContext();
  const { courierFulfillmentType } = useStockListContext();
  const { setHasItemIneligibleForCourier, setHasSeenSwitchModal } =
    useIneligibleForCourierStore();
  const { applyEligiblePromotionRewards, claimedPromotionRewardItemIds } =
    useStorefrontPromotion();

  const ineligibleItems = order?.OrderItems?.filter((item) => {
    if (!courierFulfillmentType) return;

    return isIneligibleForFulfillmentType({
      fulfillmentType: courierFulfillmentType,
      item: item.Product,
    });
  });

  const onRemove = () => {
    setLoading(true);
    ineligibleItems?.forEach((item) => {
      modifyOrderItem?.({
        productItem: {
          ...item.Product,
          ProductPriceTier: item.Product.ProductPriceTier
            ? {
                ...item.Product.ProductPriceTier,
                fullPrice: item.fullPrice,
              }
            : null,
        },
        quantity: 0,
        applyEligiblePromotionRewards: ({ order }) => {
          return applyEligiblePromotionRewards({
            order,
            claimedPromotionRewardItemIds,
          });
        },
      });
    });

    updateFulfillmentType();

    setHasItemIneligibleForCourier(false);
    setHasSeenSwitchModal(false);
    setLoading(false);
    onDismiss();
  };

  const getContainerDisplayText = (
    containerType: ContainerTypeEligibilityContainerTypesType,
  ) => {
    switch (containerType) {
      case "cask":
        return "casks";
      case "plasticKeg":
        return "plastic kegs";
      case "steelKeg":
        return "steel kegs";
      default:
        return "";
    }
  };

  const getEligibilityText = () => {
    let message = "This supplier can only send small-pack";

    courierFulfillmentType?.ContainerTypeEligibilityRules.forEach((rule) => {
      message = `${message}, ${rule.volume}${rule.unit} ${getContainerDisplayText(rule.containerType)}`;
    });

    return `${message} via courier.`;
  };

  return (
    <>
      <DialogContent className="flex flex-col gap-4">
        <span className="font-semibold">Remove the following items:</span>
        <div className="flex w-full flex-col gap-4 rounded-lg border border-zinc-300 p-4">
          {ineligibleItems?.map((item) => {
            const nameParts = item.Product.name?.split(" • ");
            return (
              <div key={item.id} className="flex items-center gap-4">
                <ProfilePicture logoUrl={item.Product.Parent?.imageUrl} />
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {nameParts?.[1] ?? "Undefined container"}
                  </span>
                  <span className="text-sm">{nameParts?.[0] ?? ""}</span>
                </div>
              </div>
            );
          })}
        </div>

        <Alert
          className="w-full"
          type="information"
          title="Why can't these items be sent by courier?"
          message={getEligibilityText()}
        />
      </DialogContent>
      <DialogCTA className="w-full">
        <div className="flex w-full flex-col-reverse md:w-auto md:flex-row">
          <Button
            color="transparent"
            onClick={onDismiss}
            className="w-full md:w-auto"
          >
            Keep pallet delivery
          </Button>
          <Button
            onClick={onRemove}
            loading={loading}
            className="w-full md:w-auto"
          >
            Remove and switch to courier
          </Button>
        </div>
      </DialogCTA>
    </>
  );
};
